import { useEffect } from "react";
import {
  LDSButton,
  LDSDropDown,
  LDSHeading,
  LDSIconTextField,
  LDSMessages,
  LDSOrderList,
} from "@resmed/lumina-design-system";
import Icons from "@resmed/lumina-design-system-assets/dist/icons";
import ParticipantListItem from "../../components/ParticipantListItem";
import { SkeletonDropdown, SkeletonParticipantList } from "../../components/skeleton";
import type { StaffParticipant } from "../../features/chat/types";
import { useChatSidebarHeader } from "../../components/ChatSidebar/ChatSidebar";
import { CREATE_CHAT_STRINGS } from "../CreateChat/constants";
import { useAddParticipantViewModel } from "./AddParticipantViewModel";
import floppyIcon from "../../../assets/floppy.svg";
import usePaginatedScroll from "../../hooks/usePaginatedScroll";

const AddParticipant = () => {
  const vm = useAddParticipantViewModel();
  const { setHeaderOverride } = useChatSidebarHeader();
  const { containerRef, rememberScrollPosition } = usePaginatedScroll(
    vm.participants.length,
    vm.userSearch,
  );

  useEffect(() => {
    setHeaderOverride({
      title: "Add participants",
      onClose: vm.onCancel,
    });

    return () => setHeaderOverride(null);
  }, [setHeaderOverride, vm.onCancel]);

  const handleParticipantScroll = (e: React.UIEvent<HTMLDivElement>) => {
    rememberScrollPosition(e);
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 150) {
      vm.onLoadMoreParticipants();
    }
  };

  return (
    <div className="lds-flex lds-flex-col lds-h-full lds-min-h-0 lds-bg-white">
      {/* Facility (locked) + search */}
      <div className="lds-shrink-0 lds-flex lds-flex-col lds-gap-4 lds-px-6 lds-pt-4 lds-pb-3">
        <div className="lds-w-full md:lds-w-[48%]">
          {vm.facilitiesLoading ? (
            <SkeletonDropdown />
          ) : (
            <LDSDropDown
              id="add-participant-facility"
              label="Facility"
              placeholder="Loading facility..."
              options={vm.facilityName ? [{ label: vm.facilityName, value: vm.facilityName }] : []}
              optionLabel="label"
              optionValue="value"
              value={vm.facilityName || null}
              size="medium"
              disabled
              showClear={false}
            />
          )}
        </div>

        <div className="lds-w-full md:lds-w-[48%]">
          <LDSIconTextField
            icon={Icons.FaMagnifyingGlass}
            iconPosition="left"
            placeholder="Search participants..."
            size="medium"
            value={vm.userSearch}
            onChange={(e) => vm.onUserSearchChange(e.target.value)}
            containerClassName="lds-w-full"
          />
        </div>
      </div>

      {/* Participant list */}
      <div
        ref={containerRef}
        className="lds-flex-1 lds-overflow-y-auto lds-px-6 lds-pb-4"
        onScroll={handleParticipantScroll}
      >
        {vm.participantsLoading ? (
          <SkeletonParticipantList />
        ) : vm.participants.length > 0 ? (
          <LDSOrderList
            value={vm.participants}
            dataKey="ssoId"
            controls={false}
            dragdrop={false}
            ariaLabel="Select participants"
            rootClassName="create-chat-participant-list lds-w-full"
            itemTemplate={(participant: StaffParticipant) => {
              const isExisting = vm.existingExternalIds.has(participant.ssoId);
              return (
                <ParticipantListItem
                  displayName={participant.displayName}
                  roleName={participant.role}
                  avatar={null}
                  showCheckbox
                  checked={isExisting || vm.selectedSsoIds.includes(participant.ssoId)}
                  disabled={isExisting}
                  onToggle={isExisting ? undefined : () => vm.onToggleSsoId(participant.ssoId)}
                  borderless
                  inlineDetails
                />
              );
            }}
          />
        ) : (
          <LDSHeading
            size="h5"
            className="lds-font-normal lds-text-sm lds-text-textcolor-caption lds-py-4 lds-text-center"
          >
            No participants found.
          </LDSHeading>
        )}
        {vm.isFetchingNextPage && <SkeletonParticipantList count={3} />}

        {vm.selectedSsoIds.length > 0 && (
          <LDSHeading size="h5" className="lds-text-sm lds-text-textcolor-caption lds-mt-2">
            {vm.selectedSsoIds.length} new participant
            {vm.selectedSsoIds.length !== 1 ? "s" : ""} selected
          </LDSHeading>
        )}

        {vm.addError && (
          <div className="lds-mt-3">
            <LDSMessages
              id="add-participant-error"
              severity="danger"
              detail={vm.addError}
              sticky
              closable={false}
            />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="lds-flex lds-items-center lds-gap-2 lds-shrink-0 lds-px-6 lds-py-4 lds-border-t lds-border-bordercolor-weak">
        <LDSButton
          id="add-participant-add"
          label="Save"
          icon={floppyIcon}
          iconPos="left"
          severity="success"
          disabled={!vm.canAdd || vm.isAdding || vm.isValidating}
          loading={vm.isAdding || vm.isValidating}
          onClick={vm.onAdd}
        />
        <LDSButton
          id="add-participant-cancel"
          label="Cancel"
          severity="secondary"
          outlined
          onClick={vm.onCancel}
        />
      </div>

      {/* Rejected participants dialog */}
      {vm.rejectedDialogVisible && (
        <div
          className="lds-fixed lds-inset-0 lds-z-50 lds-flex lds-items-center lds-justify-center"
          role="dialog"
          aria-modal="true"
        >
          <div className="lds-absolute lds-inset-0 lds-bg-black/40" />
          <div className="lds-relative lds-bg-white lds-rounded-lg lds-shadow-xl lds-w-full lds-max-w-sm lds-mx-4 lds-flex lds-flex-col lds-gap-4 lds-p-6">
            <LDSMessages
              id="rejected-participants-warning"
              severity="warning"
              detail={CREATE_CHAT_STRINGS.rejectedParticipantsWarning}
              sticky
              subtle
              closable={false}
            />

            <div className="lds-flex lds-flex-col lds-gap-2">
              <LDSHeading
                size="h5"
                className="lds-text-xs lds-font-semibold lds-text-textcolor-caption lds-uppercase lds-tracking-wide"
              >
                Participants
              </LDSHeading>
              <div className="lds-max-h-48 lds-overflow-y-auto lds-space-y-2">
                {vm.rejectedParticipants.map((p) => (
                  <ParticipantListItem
                    key={p.ssoId}
                    displayName={p.displayName}
                    roleName={p.role}
                    avatar={null}
                  />
                ))}
              </div>
            </div>

            <div className="lds-flex lds-flex-col lds-gap-2">
              <LDSButton
                id="proceed-with-others-btn"
                label={CREATE_CHAT_STRINGS.proceedWithOthers}
                disabled={!vm.canProceedWithOthers}
                onClick={vm.onProceedWithOthers}
              />
              <LDSButton
                id="rejected-cancel-btn"
                label="Cancel"
                severity="secondary"
                outlined
                onClick={vm.onCancelRejected}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddParticipant;
